// src/components/layout/AppShell.tsx
import React, { useEffect, useRef } from "react";
import { Flex, HStack, Box, useColorModeValue } from "@chakra-ui/react";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import MobileSidebar from "./MobileSidebar";
import { NavProvider } from "../../context/NavContext";
import { useLocation } from "react-router-dom";
import { useFormTheme } from "../../hooks/useFormTheme";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { triggerOnboardingOnce } from "../../helpers/onboarding";
import {
  buildLayoutTourSteps,
  buildTeachingThemeTourSteps,
  runOnboardingTour,
} from "../../helpers/onboardingTour";

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const bg = useColorModeValue("#F2F4F7", "#1A1D21");
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { lightGradient, darkGradient } = useFormTheme();
  const { t } = useTranslation();
  const { appUserProfile } = useAuth();

  useEffect(() => {
    // Scroll to top when pathname changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!appUserProfile) return;

    const isTeachingsRoute = location.pathname.startsWith("/teachings");
    const steps: ReturnType<typeof buildLayoutTourSteps> = [];

    triggerOnboardingOnce("layout", () => {
      steps.push(...buildLayoutTourSteps(t));
    });

    if (isTeachingsRoute) {
      triggerOnboardingOnce("teachingThemeFilter", () => {
        steps.push(...buildTeachingThemeTourSteps(t));
      });
    }

    if (steps.length > 0) {
      runOnboardingTour(steps, t, undefined, "layout-onboarding");
    }
  }, [appUserProfile, location.pathname, t]);

  return (
    <NavProvider>
      <HStack minH="100vh" direction="row" w="full" gap={0}>
        <Sidebar /> <MobileSidebar />
        <Flex
          ref={scrollContainerRef}
          direction="column"
          justifyContent="start"
          h="100vh"
          w="full"
          overflowY="auto"
          bgColor={bg}
          position="relative"
          // bgGradient={{ ...lightGradient, md: "none", lg: "none" }}
          _dark={{ bgGradient: { base: darkGradient, md: "none" } }}
        >
          <Navbar />
          <Box pt={{ base: "24px", lg: 0 }} w="full">
            {children}
          </Box>

          <Footer />
        </Flex>
      </HStack>
    </NavProvider>
  );
};

export default Layout;
